import { useEffect } from 'react';
import {
  Home,
  Users,
  Receipt,
  CreditCard,
  BarChart3,
  Settings,
  Lock,
  X,
  ShoppingCart,
  ChevronLeft,
  ChevronRight,
  Landmark,
  FileText,
  Banknote,
  Tag,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBrandingStore } from '@/stores/branding-store';
import { useAuthStore } from '@/stores/auth-store';
import { useModules } from '@/hooks/use-modules';
import { useLocations } from '@/hooks/use-locations';
import { useSidebarStore } from '@/stores/sidebar-store';
import { useLocationStore } from '@/stores/location-store';
import { usePosNavStore } from '@/stores/pos-nav-store';
import { usePermissions, type PermissionTab } from '@/hooks/use-permissions';
import { PosSidebarNav } from './pos-sidebar-nav';

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  // Settings stays with the role rather than a permission.
  minRole?: 'owner' | 'admin';
  // The tab opens with any key of this section. Locked when none is held.
  tab?: PermissionTab;
  moduleName?: string; // maps to a tenant module for greying out
  // Which field on the module grant to gate on. Defaults to 'isActive' (the
  // merchant-toggled modules). Single-phase modules like 'reconciliation'
  // have no merchant-side toggle — the super admin's gate is the only
  // switch — so they gate on 'superAdminEnabled' instead.
  moduleGateField?: 'isActive' | 'superAdminEnabled';
  // Remove the item outright when its module gate is closed, instead of the
  // default greyed-out-but-visible treatment. A dead link to a feature the
  // business was never sold is noise rather than a hint that it exists.
  hideWhenModuleOff?: boolean;
  // Treat the module as on until the API says otherwise. For a module that is
  // on by default (Reports), so it doesn't flash greyed out while /modules loads.
  moduleDefaultOn?: boolean;
}

// An ESL-only client (external POS or standalone shelf labels) is not a
// payments merchant and has no till. Showing them Receive Payments, Send
// Payments, POS Management or Reports would advertise products they cannot buy
// and did not sign up for, so those entries do not exist for that account kind —
// this is the menu, not a greyed-out hint.
const ESL_TENANT_HREFS = new Set(['/', '/esl', '/users', '/settings']);

const navItems: NavItem[] = [
  { label: 'Home', href: '/', icon: Home },
  { label: 'Users', href: '/users', icon: Users, tab: 'users' },
  {
    label: 'Receive Payments',
    href: '/receive-payments',
    icon: Receipt,
    moduleName: 'collections',
    tab: 'receivePayments',
  },
  {
    label: 'POS Management',
    href: '/pos-management',
    icon: ShoppingCart,
    moduleName: 'products',
    tab: 'posManagement',
  },
  {
    label: 'Send Payments',
    href: '/send-payments',
    icon: CreditCard,
    moduleName: 'payables',
    tab: 'sendPayments',
  },
  {
    label: 'Reports',
    href: '/reports',
    icon: BarChart3,
    tab: 'reports',
    moduleName: 'reporting',
    moduleGateField: 'superAdminEnabled',
    moduleDefaultOn: true,
  },
  {
    label: 'Transactions',
    href: '/funding-transactions',
    icon: Banknote,
    tab: 'transactions',
    moduleName: 'funding_transactions',
    moduleGateField: 'superAdminEnabled',
  },
  {
    label: 'Reconciliation',
    href: '/reconciliation',
    icon: Landmark,
    tab: 'reconciliation',
    moduleName: 'reconciliation',
    moduleGateField: 'superAdminEnabled',
  },
  {
    label: 'Statements',
    href: '/statements',
    icon: FileText,
    tab: 'statements',
    moduleName: 'statements',
    moduleGateField: 'superAdminEnabled',
    hideWhenModuleOff: true,
  },
  {
    label: 'Shelf Labels',
    href: '/esl',
    icon: Tag,
    tab: 'shelfLabels',
    moduleName: 'esl',
    // Two-phase: the super admin opens the platform gate, the merchant then
    // switches it on, so the default 'isActive' gate is the right one. Hidden
    // outright when off — a store with no label hardware has nothing to see,
    // and while the platform switch is off the module is absent entirely.
    hideWhenModuleOff: true,
  },
  { label: 'Settings', href: '/settings', icon: Settings, minRole: 'admin' },
];

export function Sidebar() {
  const { config } = useBrandingStore();
  const { tenant } = useAuthStore();
  const { selectedLocationId } = useLocationStore();
  const { data: modulesData } = useModules(selectedLocationId);
  const { data: locationsData } = useLocations();
  const modules = modulesData?.data ?? [];
  const allLocations = locationsData?.data ?? [];
  const selectedLocation = allLocations.find((l: any) => l.id === selectedLocationId);
  const currentPath = window.location.hash.slice(1) || '/';
  const permissions = usePermissions();
  const isTenantInactive = tenant && !tenant.isActive;
  const isLocationPending = !!selectedLocation && !selectedLocation.isActive;

  const collapsed = useSidebarStore((s) => s.collapsed);
  const toggle = useSidebarStore((s) => s.toggle);
  const mobileOpen = useSidebarStore((s) => s.mobileOpen);
  const setMobileOpen = useSidebarStore((s) => s.setMobileOpen);

  // Inside POS Management (the tabbed page and its sub-routes) the sidebar
  // shows that page's sections instead of the main menu, with a way back.
  // The sections come from the page itself, so until it has published them
  // (a cold deep link into a sub-route) the main menu stays.
  const posItems = usePosNavStore((s) => s.items);
  const pathOnly = currentPath.split('?')[0];
  const showPosNav = (pathOnly === '/pos-management' || pathOnly.startsWith('/pos-management/')) && posItems.length > 0;

  // Auto-close on hash change (covers all navigation)
  useEffect(() => {
    const onHashChange = () => setMobileOpen(false);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [setMobileOpen]);

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          // Base styles (shared)
          'flex h-screen flex-col border-r border-border bg-white transition-all duration-300 ease-in-out',
          // Desktop width: collapsed or expanded
          collapsed ? 'w-[72px]' : 'w-64',
          // Desktop: static, always visible
          'hidden lg:flex',
          // Mobile: fixed overlay, conditionally shown (always expanded)
          mobileOpen && 'fixed inset-y-0 left-0 z-50 flex !w-64',
        )}
      >
        {/* Logo / Brand */}
        <div
          className={cn(
            'flex h-16 items-center border-b border-border',
            collapsed ? 'justify-center px-2' : 'justify-between px-5',
          )}
        >
          {!collapsed && (
            <div className="flex items-center overflow-hidden">
              <img
                src={config.logoUrl || '/logo.png'}
                alt={config.appName}
                className="h-9 w-auto max-w-[180px] object-contain"
              />
            </div>
          )}
          {/* Toggle button -- visible only on desktop */}
          <button
            type="button"
            onClick={toggle}
            className="hidden lg:inline-flex items-center justify-center rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
          </button>
          {/* Close button -- visible only on mobile */}
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="inline-flex items-center justify-center rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-foreground lg:hidden"
            aria-label="Close navigation menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        {showPosNav ? (
          // The drawer is always expanded, whatever the desktop toggle says.
          <PosSidebarNav collapsed={collapsed && !mobileOpen} />
        ) : (
          <nav className={cn('flex-1 overflow-y-auto py-4', collapsed ? 'px-2' : 'px-3')}>
            <ul className="space-y-1">
              {navItems
                .filter((item) => (tenant?.type === 'esl' ? ESL_TENANT_HREFS.has(item.href) : true))
                .filter((item) => {
                  if (!item.hideWhenModuleOff || !item.moduleName) return true;
                  const gate = item.moduleGateField ?? 'isActive';
                  return modules.find((m) => m.moduleName === item.moduleName)?.[gate] === true;
                })
                .map((item) => {
                  const Icon = item.icon;
                  const hasRoleAccess =
                    (!item.minRole || permissions.atLeast(item.minRole)) &&
                    (!item.tab || permissions.canOpen(item.tab));

                  // Check if the item's module is active (if it maps to a module)
                  const gateField = item.moduleGateField ?? 'isActive';
                  const moduleActive = item.moduleName
                    ? (modules.find((m) => m.moduleName === item.moduleName)?.[gateField] ?? !!item.moduleDefaultOn)
                    : true;

                  const isDisabled = !!isTenantInactive || isLocationPending || !hasRoleAccess || !moduleActive;

                  const isActive =
                    !isDisabled &&
                    (currentPath === item.href || (item.href !== '/' && currentPath.startsWith(item.href + '/')));

                  return (
                    <li key={item.label}>
                      {isDisabled ? (
                        <div
                          className={cn(
                            'flex items-center rounded-lg py-2 text-sm font-medium text-muted-foreground/50 cursor-not-allowed select-none',
                            collapsed ? 'justify-center px-2' : 'gap-3 px-3',
                          )}
                          title={
                            collapsed
                              ? item.label
                              : isTenantInactive
                                ? 'Complete onboarding to access this page'
                                : isLocationPending
                                  ? 'This location is pending activation'
                                  : !hasRoleAccess
                                    ? 'You do not have permission to access this page'
                                    : `${item.label} module is not active`
                          }
                        >
                          <Icon className="h-5 w-5 shrink-0" />
                          {!collapsed && (
                            <>
                              <span className="flex-1">{item.label}</span>
                              {!hasRoleAccess && <Lock className="h-3.5 w-3.5" />}
                            </>
                          )}
                        </div>
                      ) : (
                        <a
                          href={`#${item.href}`}
                          title={collapsed ? item.label : undefined}
                          className={cn(
                            'flex items-center rounded-lg py-2 text-sm font-medium transition-colors',
                            collapsed ? 'justify-center px-2' : 'gap-3 px-3',
                            isActive
                              ? 'bg-primary/10 text-primary'
                              : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                          )}
                        >
                          <Icon className="h-5 w-5 shrink-0" />
                          {!collapsed && <span>{item.label}</span>}
                        </a>
                      )}
                    </li>
                  );
                })}
            </ul>
          </nav>
        )}
      </aside>
    </>
  );
}
